'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { powerMonitor } from '@todesktop/client-core';
import { useEffect } from 'react';
import { listenToDesktopEvent } from '@/lib/desktop-app-listener';

/** Waking and unlocking usually happen together; one refresh covers both. */
export const WAKE_REFRESH_COOLDOWN_MS = 5_000;
const WAKE_EVENTS = ['resume', 'unlock-screen'] as const;

/**
 * Refetches on-screen data after the computer wakes or unlocks. The desktop app usually sits
 * hidden in the tray, so no window focus or visibility change triggers React Query's own refetch,
 * and the tray list and app badge would keep the inbox from before the computer slept. The realtime
 * socket reconnects on its own once its next heartbeat goes unanswered.
 */
export function DesktopWakeRefresh() {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = () => {
      let lastRefreshAt = Number.NEGATIVE_INFINITY;
      const refresh = () => {
        const now = Date.now();
        if (now - lastRefreshAt < WAKE_REFRESH_COOLDOWN_MS) {
          return;
        }
        lastRefreshAt = now;
        queryClient.invalidateQueries();
      };
      const stopListeners = WAKE_EVENTS.map(event => listenToDesktopEvent(`the desktop ${event} event`, powerMonitor.on(event, refresh)));
      return () => {
        for (const stopListening of stopListeners) {
          stopListening();
        }
      };
    };
    t1 = [queryClient];
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}